import {CLStore} from 'common/src/redux/store';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import {addOrUpdateCardInputSchema} from '../../../../schemas/baseSchemas';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import standaloneAgent from 'common/src/modules/shared/copilot/utils/creators/standaloneAgent';
import {responseSchema} from '../../promptToCardAgentV2.schemas';
import getSetupAgentModel from '../../../../utils/getSetupAgentModel';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {cardTypeToName} from '../../../../campaignSetupAgent.config';
import getPromptToCardAgentInstructions from '../../../promptToCardAgent/utils/getPromptToCardAgentInstructions';
import setCardNameTool from '../general/setCardNameTool';
import setCardDescriptionTool from '../general/setCardDescriptionTool';
import getCardDetailsTool from '../general/getCardDetailsTool';
import preProcessStandaloneAgent from '../../utils/general/preProcessStandaloneAgent';
import postProcessStandaloneAgent from '../../utils/general/postProcessStandaloneAgent';
import {CreativeCardScratchpadState} from '../../scratchpad/creativeCardScratchpad.types';
import getOrCreateCreativeCard from '../../utils/creative/getOrCreateCreativeCard';
import setCreativeTool from './setCreativeTool';
import setIsFallbackTool from './setIsFallbackTool';
import getCreativesTool from '../../../promptToCardAgent/tools/creatives/getCreativesTool';

const cardSpecificInstructions = `### What is a Creative card
A Creative card is a leaf card in the decision tree — it defines which creative template is used to render the final ad. A creative card without a specific creative assigned is called a "placeholder creative card" — the creative will be chosen later.

### Creative card rules
- If no creative is specified in the prompt, create a placeholder creative card (no creative assigned).
- If a creative ID is provided, use it directly.
- If other creative details are provided instead of an ID, search for the creative to identify its ID.
- Landing page and UTM parameters cannot be changed; if the user asks, refuse and say it is not supported.
- A fallback creative must not support products, location, content, or weather.

### Property Dependencies
- If the creative ID is not known and creative details were provided, creatives must be searched (getCreatives) before one can be set.
- The creative and the fallback flag are independent of each other — neither depends on the other being set first.`;

export default function addOrUpdateCreativeCardToolV2(store: CLStore) {
  async function execute(
    params: ToolExecuteParams<typeof addOrUpdateCardInputSchema, CreativeCardScratchpadState>,
  ) {
    const {toolUtils, callChain} = params;

    const {promptToUse, normalizedCardId, card} = await preProcessStandaloneAgent(
      params,
      store,
      getOrCreateCreativeCard,
    );

    const standaloneAgentDef = standaloneAgent({
      name: V2CardAgentToolName.AddOrUpdateCreativeCard,
      role: `Your purpose is to convert a natural language prompt to a ${
        cardTypeToName[NodeType.CREATIVE]
      } card`,
      instructions: getPromptToCardAgentInstructions({
        cardType: NodeType.CREATIVE,
        cardId: normalizedCardId,
        cardSpecificInstructions,
      }),
      tools: [
        setCardNameTool(),
        setCardDescriptionTool(),
        getCardDetailsTool(),
        getCreativesTool(store),
        setCreativeTool(store),
        setIsFallbackTool(),
      ],
      responseSchema,
      model: getSetupAgentModel(),
    });

    const toolScratchpad = {card};

    const result = await toolUtils.invokeStandaloneAgent({
      agent: standaloneAgentDef,
      prompt: promptToUse,
      callChain,
      scratchpad: toolScratchpad,
    });

    return postProcessStandaloneAgent({
      result: {status: result.status, chainOfThoughts: result.chainOfThoughts},
      scratchpad: toolScratchpad,
      store,
      cardId: normalizedCardId,
    });
  }

  return tool({
    name: V2CardAgentToolName.AddOrUpdateCreativeCard,
    description:
      'Add or update a Creative card. Allow to set creative and define creative as fallback.',
    displayName: 'Working on creative card',
    inputSchema: addOrUpdateCardInputSchema,
    execute,
  });
}
